import { useEffect, useRef, useState, type FC, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { SlidersHorizontal } from "lucide-react";
import type {
  PaidMediaOverviewFiltersData,
  PaidMediaOverviewGranularity,
  PaidMediaOverviewPlatformFilter,
} from "src/services/PaidMediaOverviewAPI";
import OrganicDateRangePicker from "src/components/common/OrganicDateRangePicker/OrganicDateRangePicker";
import SelectMenu from "src/components/common/SelectMenu/SelectMenu";
import { DashboardExportButton } from "src/components/common";
import type { OverviewAttributionSource } from "../../utils/PaidMediaOverviewAttributionContext";
import { overviewStatusLabel } from "../../utils/PaidMediaOverviewLabels";
import { OverviewSegmented } from "./OverviewPrimitives";

export interface PaidMediaOverviewHeaderState {
  attribution: OverviewAttributionSource;
  granularity: PaidMediaOverviewGranularity;
  startDate: string;
  endDate: string;
  platform: PaidMediaOverviewPlatformFilter;
  campaignId: string;
  objectiveOrNetwork: string;
  status: string;
}

interface FilterOption {
  value: string;
  label: string;
}

interface FilterRow {
  id: string;
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (_value: string) => void;
  disabled?: boolean;
}

interface PaidMediaOverviewHeaderProps {
  state: PaidMediaOverviewHeaderState;
  filters: PaidMediaOverviewFiltersData | null;
  onChange: (_patch: Partial<PaidMediaOverviewHeaderState>) => void;
  shopifyAvailable: boolean;
  pdfContainerRef: RefObject<HTMLDivElement>;
  exportDisabled: boolean;
  onBeforePdfCapture?: () => Promise<void>;
  onAfterPdfCapture?: () => void;
}

const ALL_OPTION_VALUE = "";

const FILTER_DROPDOWN_CLASS = "paid-media-overview__filter-select-dropdown";

const PaidMediaOverviewHeader: FC<PaidMediaOverviewHeaderProps> = ({
  state,
  filters,
  onChange,
  shopifyAvailable,
  pdfContainerRef,
  exportDisabled,
  onBeforePdfCapture,
  onAfterPdfCapture,
}) => {
  const { t } = useTranslation();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const moreFiltersRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!filtersOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }
      if (moreFiltersRef.current?.contains(target)) {
        return;
      }
      if (target.closest(`.${FILTER_DROPDOWN_CLASS}`)) {
        return;
      }
      setFiltersOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setFiltersOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [filtersOpen]);

  const allLabel = t("paidMediaOverview.header.filterAll");
  const hasCampaignLevelFilter = Boolean(
    state.campaignId || state.objectiveOrNetwork || state.status
  );
  const shopifyToggleDisabled = !shopifyAvailable || hasCampaignLevelFilter;
  // Shopify-validated attribution reports at the platform level only, so the campaign-level
  // filters can't be honored while it's selected.
  const campaignFiltersLocked = state.attribution === "shopify";
  const shopifyToggleTooltip = !shopifyAvailable
    ? t("paidMediaOverview.header.attributionShopifyUnavailable")
    : hasCampaignLevelFilter
      ? t("paidMediaOverview.header.attributionShopifyDisabledByCampaignFilter")
      : t("paidMediaOverview.header.attributionShopifyHelp");

  const filterRows: FilterRow[] = [
    {
      id: "platform",
      label: t("paidMediaOverview.header.filters.platform"),
      value: state.platform,
      options: [
        { value: "all", label: allLabel },
        ...(filters?.platforms ?? []).map(platform => ({
          value: platform?.value,
          label: platform?.label,
        })),
      ],
      onChange: value => onChange({ platform: value as PaidMediaOverviewPlatformFilter }),
    },
    {
      id: "campaign",
      label: t("paidMediaOverview.header.filters.campaign"),
      value: state.campaignId,
      options: [
        { value: ALL_OPTION_VALUE, label: t("paidMediaOverview.header.filterAllCampaigns") },
        ...(filters?.campaigns ?? []).map(campaign => ({
          value: campaign?.campaign_id,
          label: campaign?.campaign_name,
        })),
      ],
      onChange: value => onChange({ campaignId: value }),
      disabled: campaignFiltersLocked,
    },
    {
      id: "objectiveOrNetwork",
      label: t("paidMediaOverview.header.filters.objectiveOrNetwork"),
      value: state.objectiveOrNetwork,
      options: [
        { value: ALL_OPTION_VALUE, label: allLabel },
        ...(filters?.objectives_or_networks ?? []).map(option => ({
          value: option,
          label: option,
        })),
      ],
      onChange: value => onChange({ objectiveOrNetwork: value }),
      disabled: campaignFiltersLocked,
    },
    {
      id: "classification",
      label: t("paidMediaOverview.header.filters.classification"),
      value: state.status,
      options: [
        { value: ALL_OPTION_VALUE, label: allLabel },
        ...(filters?.statuses ?? []).map(status => ({
          value: status?.value,
          label: overviewStatusLabel(t, status?.value, status?.label),
        })),
      ],
      onChange: value => onChange({ status: value }),
      disabled: campaignFiltersLocked,
    },
  ];

  return (
    <div className="paid-media-overview__header" data-pdf-block="dashboard-heading">
      <div className="paid-media-overview__header-copy">
        <h1 className="paid-media-overview__title">{t("paidMediaOverview.title")}</h1>
        <p className="paid-media-overview__subtitle">{t("paidMediaOverview.subtitle")}</p>
      </div>

      <div className="paid-media-overview__header-actions">
        <div className="paid-media-overview__header-row">
          <OverviewSegmented
            value={state.attribution}
            onChange={value => onChange({ attribution: value })}
            ariaLabel={t("paidMediaOverview.header.attributionAria")}
            options={[
              {
                value: "platform",
                label: t("paidMediaOverview.header.attributionPlatform"),
                tooltip: { description: t("paidMediaOverview.header.attributionPlatformHelp") },
              },
              {
                value: "shopify",
                label: t("paidMediaOverview.header.attributionShopify"),
                disabled: shopifyToggleDisabled,
                tooltip: { description: shopifyToggleTooltip },
              },
            ]}
          />
          <DashboardExportButton
            dashboardName="Paid Media Overview"
            dateRange={{ startDate: state.startDate, endDate: state.endDate }}
            containerRef={pdfContainerRef}
            disabled={exportDisabled}
            onBeforeCapture={onBeforePdfCapture}
            onAfterCapture={onAfterPdfCapture}
          />
        </div>

        <div className="paid-media-overview__header-row paid-media-overview__header-row--bottom">
          <div className="paid-media-overview__control paid-media-overview__control--wide">
            <OrganicDateRangePicker
              startDate={state.startDate}
              endDate={state.endDate}
              onRangeChange={(range: { startDate: string; endDate: string }) =>
                onChange({ startDate: range.startDate, endDate: range.endDate })
              }
            />
          </div>
          <div className="paid-media-overview__more-filters" ref={moreFiltersRef}>
            <button
              type="button"
              className="paid-media-overview__more-filters-trigger"
              onClick={() => setFiltersOpen(open => !open)}
              aria-expanded={filtersOpen}
            >
              <SlidersHorizontal size={14} aria-hidden />
              {t("paidMediaOverview.header.moreFilters")}
            </button>
            {filtersOpen ? (
              <div
                className="paid-media-overview__more-filters-panel"
                role="dialog"
                aria-label={t("paidMediaOverview.header.moreFiltersPanelAria")}
              >
                {campaignFiltersLocked ? (
                  <p className="paid-media-overview__filter-lock-note">
                    {t("paidMediaOverview.header.attributionShopifyLocksCampaignFilters")}
                  </p>
                ) : null}
                {filterRows.map(row => (
                  <div key={row.id} className="paid-media-overview__filter-row">
                    <span className="paid-media-overview__filter-label">{row.label}</span>
                    <SelectMenu
                      value={row.value}
                      options={row.options}
                      onChange={row.onChange}
                      className="paid-media-overview__filter-select"
                      dropdownClassName="paid-media-overview__filter-select-dropdown"
                      ariaLabel={row.label}
                      disabled={row.disabled}
                    />
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaidMediaOverviewHeader;
